import { z } from 'zod';
import { QueryClient } from '@tanstack/react-query';
import { getUserNameAvailabilityQuery } from '../../../api/user-name-availability';
import {
  getUserNameIssue,
  USER_NAME_MAX_LENGTH,
} from '../../../utils/username';
import { Messages } from '../../../utils/message-constants';

// Welcome Email Schema
export const WelcomeEmailSchema = z.object({
  sendEmail: z.boolean(),
  templateType: z.string(),
  templateSubject: z.string(),
  templateHtml: z.string(),
  availableTemplates: z.array(z.string()),
});

export const AddressSchema = z
  .object({
    type: z.string(),
    customType: z.string().optional(),
    sourceIsStructured: z.boolean(),
    formatted: z.string(),
    poBox: z.string(),
    extendedAddress: z.string(),
    streetAddress: z.string(),
    locality: z.string(),
    region: z.string(),
    postalCode: z.string(),
    country: z.string(),
    primary: z.boolean(),
    countryCode: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    if (!val.type) {
      ctx.addIssue({
        code: 'custom',
        message: 'Type is required.',
        path: ['type'],
      });
    }
    if (val.type === 'custom' && !val.customType) {
      ctx.addIssue({
        code: 'custom',
        message: 'Custom Type is required.',
        path: ['customType'],
      });
    }
  });

export const EmailSchema = z
  .object({
    address: z.string(),
    type: z.string().optional(),
    customType: z.string().nullable().optional(),
    primary: z.boolean(),
  })
  .superRefine((val, ctx) => {
    if (!val.type) {
      ctx.addIssue({
        code: 'custom',
        message: 'Type is required.',
        path: ['type'],
      });
    }
    if (!val.address || val.address === '') {
      ctx.addIssue({
        code: 'custom',
        message: 'Address is required.',
        path: ['address'],
      });
    }
    if (val.type === 'custom' && !val.customType) {
      ctx.addIssue({
        code: 'custom',
        message: 'Custom Type is required.',
        path: ['customType'],
      });
    }
  });

export const ExternalIdSchema = z
  .object({
    value: z.string(),
    type: z.string(),
    customType: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    if (!val.type) {
      ctx.addIssue({
        code: 'custom',
        message: 'Type is required.',
        path: ['type'],
      });
    }
    if (val.type === 'custom' && !val.customType) {
      ctx.addIssue({
        code: 'custom',
        message: 'Custom Type is required.',
        path: ['customType'],
      });
    }

    if (!val.value || val.value === '') {
      ctx.addIssue({
        code: 'custom',
        message: 'Value is required.',
        path: ['value'],
      });
    }
  });

export const RelationSchema = z
  .object({
    value: z.string(),
    type: z.string(),
    customType: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    if (!val.type) {
      ctx.addIssue({
        code: 'custom',
        message: 'Type is required.',
        path: ['type'],
      });
    }
    if (val.type === 'custom' && !val.customType) {
      ctx.addIssue({
        code: 'custom',
        message: 'Custom Type is required.',
        path: ['customType'],
      });
    }

    if (!val.value || val.value === '') {
      ctx.addIssue({
        code: 'custom',
        message: 'Value is required.',
        path: ['value'],
      });
    }
  });

export const PersonAddressSchema = z.object({
  formattedValue: z.string(),
  poBox: z.string(),
  streetAddress: z.string(),
  extendedAddress: z.string(),
  city: z.string(),
  region: z.string(),
  postalCode: z.string(),
  country: z.string(),
  countryCode: z.string(),
  type: z.string(),
});

export const MetaDataSchema = z.object({
  primary: z.boolean(),
});

export const OrganizationSchema = z.object({
  name: z.string(),
  title: z.string().nullable(),
  primary: z.boolean(),
  type: z.string().nullable().optional(),
  customType: z.string().optional(),
  department: z.string().nullable(),
  symbol: z.string(),
  location: z.string(),
  description: z.string(),
  domain: z.string(),
  costCenter: z.string(),
  metadata: MetaDataSchema.optional(),
});

export const PhoneSchema = z
  .object({
    value: z.string(),
    type: z.string(),
    customType: z.string().optional(),
    primary: z.boolean(),
  })
  .superRefine((val, ctx) => {
    if (!val.type) {
      ctx.addIssue({
        code: 'custom',
        message: 'Type is required.',
        path: ['type'],
      });
    }
    if (val.type === 'custom' && !val.customType) {
      ctx.addIssue({
        code: 'custom',
        message: 'Custom Type is required.',
        path: ['customType'],
      });
    }

    if (!val.value || val.value === '') {
      ctx.addIssue({
        code: 'custom',
        message: 'Phone Number is required.',
        path: ['value'],
      });
    }
  });

export const WebsiteSchema = z
  .object({
    url: z.string(),
    type: z.string(),
    customType: z.string().optional(),
    primary: z.boolean(),
  })
  .superRefine((val, ctx) => {
    if (!val.type) {
      ctx.addIssue({
        code: 'custom',
        message: 'Type is required.',
        path: ['type'],
      });
    }
    if (val.type === 'custom' && !val.customType) {
      ctx.addIssue({
        code: 'custom',
        message: 'Custom Type is required.',
        path: ['customType'],
      });
    }

    if (!val.url || val.url === '') {
      ctx.addIssue({
        code: 'custom',
        message: 'URL is required.',
        path: ['url'],
      });
    }
  });

export const IMSSchema = z
  .object({
    type: z.string(),
    customType: z.string().optional(),
    protocol: z.string().optional(),
    customProtocol: z.string().optional(),
    im: z.string(),
    primary: z.boolean(),
  })
  .superRefine((val, ctx) => {
    if (!val.type) {
      ctx.addIssue({
        code: 'custom',
        message: 'Type is required.',
        path: ['type'],
      });
    }
    if (val.type === 'custom' && !val.customType) {
      ctx.addIssue({
        code: 'custom',
        message: 'Custom Type is required.',
        path: ['customType'],
      });
    }

    if (!val.im || val.im === '') {
      ctx.addIssue({
        code: 'custom',
        message: 'IM is required.',
        path: ['im'],
      });
    }
  });

export const NameSchema = z.object({
  familyName: z.string().min(1, 'Last Name is required.'),
  givenName: z.string().min(1, 'First Name is required.'),
  fullName: z.string(),
});

export const LocationSchema = z.object({
  area: z.string(),
  buildingId: z.string(),
  deskCode: z.string(),
  floorName: z.string(),
  floorSection: z.string(),
  type: z.string(),
});

export const GenderSchema = z
  .object({
    addressMeAs: z.string(),
    type: z.enum(['male', 'female', 'other', 'unknown', '']),
    customType: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.addressMeAs !== '' && data.type === '') {
      ctx.addIssue({
        code: 'custom',
        message: 'Gender must be selected if "Address Me As" is filled in.',
        path: ['type'],
      });
    }
  });

export const SchemaValueSchema = z.object({
  fieldDisplayName: z.string(),
  value: z.string().optional(),
  values: z.array(z.string()),
});

export const CustomSchemaSchema = z.object({
  values: z.array(SchemaValueSchema),
  schemaName: z.string(),
});

export const MemberSchema = z.object({
  email: z.string(),
  role: z.enum(['OWNER', 'MANAGER', 'MEMBER']),
  canActOn: z.boolean(),
});

export const AddUserGroupSchema = z.object({
  groupId: z.string(),
  member: MemberSchema,
});

// Setup User Step Schema
export const SetupUserSchema = z.object({
  userName: z.string().min(1, 'Username is required.').optional(),
  domain: z.string().min(1, 'Domain is required.').optional(),
  name: NameSchema.nullable().optional(),
  password: z.string().min(1, 'Password is required.').nullable().optional(),
  confirmPassword: z
    .string()
    .min(1, 'Confirm Password is required.')
    .optional(),
  orgUnitPath: z.string().optional(),
  changePasswordAtNextLogin: z.boolean().optional(),
  cPanelRole: z.string().min(1, 'cPanel Role is required.').optional(),
  gPanelRole: z.string().min(1, 'gPanel Role is required.').optional(),
  includeInGlobalAddressList: z.boolean().optional(),
  creationTime: z.number().optional(),
});

// Groups Step Schema
export const GroupsSchema = z.object({
  groups: z.array(AddUserGroupSchema).optional(),
});

// Employee and Profile Information Step Schema
export const InformationSchema = z.object({
  externalIds: z.array(ExternalIdSchema).nullable().optional(),
  phones: z.array(PhoneSchema).nullable().optional(),
  relations: z.array(RelationSchema).nullable().optional(),
  organizations: z.array(OrganizationSchema).nullable().optional(),

  addresses: z.array(AddressSchema).nullable().optional(),
  emails: z.array(EmailSchema).nullable().optional(),
  gender: GenderSchema.nullable().optional(),
  ims: z.array(IMSSchema).nullable().optional(),
  notes: z.string().nullable().optional(),
  websites: z.array(WebsiteSchema).nullable().optional(),
});

// Welcome Recipient Step Schema
export const WelcomeRecipientSchema = z
  .object({
    usersToNotify: z.array(z.string()).optional(),
    ccTo: z.array(z.string()).optional(),
    bccTo: z.array(z.string()).optional(),
    welcomeEmailModel: WelcomeEmailSchema.optional(),
    includeCreatedUser: z.boolean().optional(),
    scheduledSendDate: z.date().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.welcomeEmailModel?.sendEmail &&
      (!data.usersToNotify || data.usersToNotify.length === 0) &&
      !data.includeCreatedUser
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one email is required.',
        path: ['usersToNotify'],
      });
    }
  });

// Policies Step Schema
export const PoliciesSchema = z.object({
  policies: z.array(z.any()).optional(),
});

// Integrations Step Schema
export const IntegrationsSchema = z.object({
  slackChannels: z.array(z.string()).optional(),
});

// User Base Step Schema
export const UserFormSchema = SetupUserSchema.and(GroupsSchema)
  .and(InformationSchema)
  .and(WelcomeRecipientSchema)
  .and(PoliciesSchema)
  .and(IntegrationsSchema);

export function getUserFormSchema(step: number, queryClient: QueryClient) {
  if (step > 0) return UserFormSchema;
  else
    return UserFormSchema.superRefine((data, ctx) => {
      const userNameIssue = data.userName
        ? getUserNameIssue(data.userName)
        : null;
      if (userNameIssue) {
        ctx.addIssue({
          code: 'custom',
          message:
            userNameIssue === 'tooLong'
              ? Messages.UserName.TooLong(USER_NAME_MAX_LENGTH)
              : Messages.UserName.Invalid,
          path: ['userName'],
        });
      }

      if (data.password && data.password.length < 8) {
        ctx.addIssue({
          code: 'custom',
          message: 'Password must be at least 8 characters long.',
          path: ['password'],
        });
      }
      if (data.confirmPassword && data.confirmPassword.length < 8) {
        ctx.addIssue({
          code: 'custom',
          message: 'Password must be at least 8 characters long.',
          path: ['confirmPassword'],
        });
      }

      if (data.password !== data.confirmPassword) {
        ctx.addIssue({
          code: 'custom',
          message: 'Confirm Password must match Password.',
          path: ['confirmPassword'],
        });
      }
    }).superRefine(async (data, ctx) => {
      const { userName, domain } = data;
      if (!userName || !domain || getUserNameIssue(userName)) return;

      try {
        const { available } = await queryClient.fetchQuery(
          getUserNameAvailabilityQuery(userName.toLowerCase(), domain)
        );
        if (!available) {
          ctx.addIssue({
            code: 'custom',
            message: Messages.UserName.Taken,
            path: ['userName'],
          });
        }
      } catch {
        ctx.addIssue({
          code: 'custom',
          message: Messages.UserName.Unverified,
          path: ['userName'],
        });
      }
    });
}

// Reference typing for User Form Schema
export type UserFormValues = z.infer<typeof UserFormSchema>;

export type Address = z.infer<typeof AddressSchema>;
export type Email = z.infer<typeof EmailSchema>;
export type ExternalId = z.infer<typeof ExternalIdSchema>;
export type IMs = z.infer<typeof IMSSchema>;
export type AddUserGroup = z.infer<typeof AddUserGroupSchema>;
